// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import appActions from 'common/support/applicationActions';
import device from 'common/support/device';
import focusManager from 'support/focus/FocusManager';
import FocusGroup from 'components/focus/FocusGroup';
import withRestoration from 'components/withRestoration';

import { getAppScalingFactor } from 'support/scalingHelper';
import { selectItems } from 'selectors/list';
import textToSpeechManager from 'support/tts/TextToSpeechManager';

import styles from './List.module.scss';

// We need to manually calculate the scaling factor (app width / 1920) since certain measurements
// are not scaled appropriately even though the element is affected by transform: scale
// getBoundingClientRect values are scaled automatically, whereas offset (ie. offsetTop/offsetLeft)
// and buffers need to be scaled manually
const scaling = getAppScalingFactor();

const ScrollDirection = {
  LEFT: 'left',
  RIGHT: 'right',
};

class List extends React.PureComponent {
  state = {
    isAnimating: false,
    left: 0,
    startIndex: 0,
    endIndex: 0,
  };

  listViewport = React.createRef();

  focusGroup = React.createRef();

  listItem = React.createRef();

  focusedIndex = 0;

  itemWidth = null;

  timeout = null;

  focusId = null;

  constructor(props) {
    super(props);
    const { restorationState, data } = props;
    this.focusId = props.focusId || focusManager.getNewUniqueId();
    this.state.endIndex = props.buffer;
    if (restorationState && data) {
      const {
        endIndex: oldEndIndex,
        startIndex: oldStartIndex,
      } = restorationState;
      let newEndIndex = oldEndIndex;
      let newStartIndex = oldStartIndex;
      if (oldEndIndex > data.length - 1) {
        newEndIndex = data.length - 1;
        const amountToShift = oldEndIndex - newEndIndex;
        newStartIndex = Math.max(0, oldStartIndex - amountToShift);
      }
      this.state = {
        ...restorationState,
        startIndex: newStartIndex,
        endIndex: newEndIndex,
      };
    }
  }

  componentDidMount() {
    const { data } = this.props;
    focusManager.addFocusChangedListener(this.onFocusChanged);
    if (data && this.listItem && this.listItem.current) {
      this.itemWidth = this.listItem.current.getBoundingClientRect().width;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, setRestorationState } = this.props;

    if (!prevProps.data && data && this.listItem && this.listItem.current) {
      this.itemWidth = this.listItem.current.getBoundingClientRect().width;
    }

    if (prevState !== this.state && this.state) {
      setRestorationState(this.state);
    }
  }

  componentWillUnmount() {
    focusManager.removeFocusChangedListener(this.onFocusChanged);
  }

  onFocusChanged = () => {
    if (!window || !focusManager.isFocusAncestor(this.focusId)) {
      return;
    }

    const focusedEl = focusManager.getFocusableElement(
      focusManager.currentFocusId,
    );
    const {
      left: rectLeft,
      right: rectRight,
    } = focusedEl.getBoundingClientRect();

    const lastFocusedIndex = this.focusedIndex;
    const offsetLeft = focusedEl.offsetLeft * scaling;
    const itemWidth = rectRight - rectLeft;
    const { peekRatio } = this.props;
    const margin = itemWidth * peekRatio;

    this.setState(
      {
        isAnimating: true,
        left: Math.min(0, -offsetLeft - margin),
      },
      () => {
        if (!this.timeout) {
          this.timeout = setTimeout(() => {
            this.timeout = null;
            this.handleClipping(ScrollDirection.RIGHT, lastFocusedIndex);
          }, 300);
        }
      },
    );
  };

  handleClipping = (scrollDirection, lastFocusedIndex) => {
    const { buffer } = this.props;
    const { startIndex, endIndex, left } = this.state;

    // Need to add these indexes together since the FocusGroup is only
    // receiving a subset of items
    const focusedIndex = lastFocusedIndex + startIndex;
    const newStartIndex = Math.max(0, focusedIndex - 1);
    const newEndIndex = newStartIndex + buffer + 1;

    if (
      (newStartIndex !== startIndex || newEndIndex !== endIndex) &&
      this.itemWidth
    ) {
      const amountToClip = Math.abs(newStartIndex - startIndex);
      const focusedEl = focusManager.getFocusableElement(
        focusManager.currentFocusId,
      );
      const offsetLeft = focusedEl.offsetLeft * scaling;
      const offsetright =
        this.itemWidth - offsetLeft - focusedEl.offsetWidth * scaling;
      this.setState(
        {
          isAnimating: false,
          startIndex: newStartIndex,
          endIndex: newEndIndex,
          left:
            newStartIndex > startIndex
              ? left + (offsetLeft / lastFocusedIndex) * amountToClip
              : left - (this.itemWidth - offsetright) * amountToClip,
        },
        () => {
          if (this.focusGroup && this.focusGroup.current) {
            this.focusGroup.current.resetFocusIndex();
          }
        },
      );
    }
  };

  onFocusIndexOverride = (action, currentIndex) => {
    let newIndex = false;

    switch (action) {
      case appActions.moveLeft:
        newIndex = currentIndex - 1;
        break;
      case appActions.moveRight:
        newIndex = currentIndex + 1;
        break;
      default:
        newIndex = false;
    }

    if (action !== appActions.moveLeft && action !== appActions.moveRight) {
      return newIndex;
    }

    const {
      left: viewportLeft,
      right: viewportRight,
    } = this.listViewport.current.getBoundingClientRect();

    const listFocusChildren = focusManager.getFocusableChildren(this.focusId);
    const newChild = listFocusChildren[newIndex];

    if (newChild) {
      const {
        left: newFocusLeft,
        right: newFocusRight,
      } = newChild.getBoundingClientRect();

      if (
        this.timeout &&
        (newFocusLeft < viewportLeft || newFocusRight > viewportRight)
      ) {
        return 'handled';
      }
    }

    return newIndex;
  };

  onEndOfNavigation = () => {
    const { data, onEndOfNavigationOverride } = this.props;
    const { startIndex } = this.state;
    const index = this.focusedIndex + startIndex;
    if (index === 0 || index === data.length - 1) {
      const { ttsEndOfNavigation } = this.props;
      textToSpeechManager.add(ttsEndOfNavigation);
      textToSpeechManager.speak();
    }
    if (onEndOfNavigationOverride) {
      onEndOfNavigationOverride(index);
    }
  };

  onFocusIndexChanged = (index, ...rest) => {
    const { onFocusIndexChanged } = this.props;
    this.focusedIndex = index;
    onFocusIndexChanged(index, ...rest);
  };

  resetIndex = async () => {
    return new Promise(res => {
      const { buffer } = this.props;
      this.focusedIndex = 0;
      this.setState(
        {
          isAnimating: false,
          left: 0,
          startIndex: 0,
          endIndex: buffer,
        },
        () => {
          const focusableItems = focusManager.getFocusableChildren(
            this.focusId,
          );
          const firstItem =
            focusableItems?.length > 0 ? focusableItems[0] : null;
          const firstItemId = focusManager.getElementFocusId(firstItem);
          focusManager.setCurrentFocus(firstItemId);
          res();
        },
      );
    });
  };

  getFocusedIndex = () => this.focusedIndex;

  render() {
    const { className, onFocusDelegated } = this.props;
    const { left, isAnimating } = this.state;
    const items = selectItems(this.props, this.state, this.listItem);

    return (
      <div className={className} ref={this.listViewport}>
        <FocusGroup
          className={classNames(
            {
              [styles.animate]: isAnimating && !device.isSlowDevice,
            },
            'text-nowrap',
          )}
          focusId={this.focusId && this.focusId.toString()}
          onFocusDelegated={onFocusDelegated}
          onFocusIndexOverride={this.onFocusIndexOverride}
          onFocusIndexChanged={this.onFocusIndexChanged}
          ref={this.focusGroup}
          style={{
            transform: `translateX(${left / scaling}px)`,
            WebkitTransform: `translateX(${left / scaling}px)`,
          }}
          horizontal
          onEndOfNavigation={this.onEndOfNavigation}
        >
          {items}
        </FocusGroup>
      </div>
    );
  }
}

List.propTypes = {
  // Ignoring because this is used internally inside the
  // selectItems selector
  // eslint-disable-next-line react/no-unused-prop-types
  renderItem: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(
    (propValue, key, componentName, location, propFullName) => {
      if (propValue[key].id || propValue[key].id === 0) {
        return null;
      }

      return new Error(
        `Invalid prop ${propFullName} supplied to ${componentName}.  Data items must contain an id.`,
      );
    },
  ).isRequired,
  buffer: PropTypes.number.isRequired,
  className: PropTypes.string,
  focusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFocusDelegated: PropTypes.func,
  onFocusIndexChanged: PropTypes.func,
  restorationState: PropTypes.shape({
    isAnimating: PropTypes.bool.isRequired,
    left: PropTypes.number.isRequired,
    endIndex: PropTypes.number.isRequired,
    startIndex: PropTypes.number.isRequired,
  }),
  setRestorationState: PropTypes.func.isRequired,
  peekRatio: PropTypes.number,
  ttsEndOfNavigation: PropTypes.string,
  onEndOfNavigationOverride: PropTypes.func,
};

List.defaultProps = {
  className: null,
  focusId: null,
  onFocusDelegated: () => {},
  onFocusIndexChanged: () => {},
  restorationState: null,
  peekRatio: 0.2,
  ttsEndOfNavigation: null,
  onEndOfNavigationOverride: () => {
    return false;
  },
};

export default withRestoration(props => props.restorationId)(List);
